import { useMemo } from 'react'
import type { ExerciseRendererProps } from './types'

/**
 * Renders the sentence with real inputs sitting inline where the blanks are,
 * so she reads the whole sentence rather than answering a detached list.
 */
export function FillBlankExercise({
  content,
  response,
  onChange,
  locked,
  result,
}: ExerciseRendererProps<'fill_blank'>) {
  const segments = useMemo(() => content.template.split('___'), [content.template])
  const values = response?.values ?? content.blanks.map(() => '')

  function setValue(index: number, next: string) {
    const updated = content.blanks.map((_, i) => (i === index ? next : (values[i] ?? '')))
    onChange({ type: 'fill_blank', values: updated })
  }

  return (
    <div className="space-y-4">
      <p className="text-lg leading-relaxed text-ink-900">
        {segments.map((segment, index) => (
          <span key={index}>
            {segment}
            {index < content.blanks.length && (
              <input
                type="text"
                value={values[index] ?? ''}
                onChange={(e) => setValue(index, e.target.value)}
                disabled={locked}
                aria-label={`Blank ${index + 1}${
                  content.blanks[index]?.hint ? `, hint: ${content.blanks[index]?.hint}` : ''
                }`}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                // Sized to the expected answer so the blank does not give away
                // the length, but stays tappable on a phone.
                className={`mx-1 inline-block min-h-10 w-32 rounded-lg px-2 text-center text-base ring-1 focus:ring-2 focus:ring-brand-600 disabled:bg-sand-50 ${
                  locked
                    ? result?.feedback.parts?.[index] === 'wrong'
                      ? 'ring-clay-300 text-clay-700'
                      : 'ring-brand-500 text-brand-700'
                    : 'ring-sand-200 bg-white'
                }`}
              />
            )}
          </span>
        ))}
      </p>

      {!locked && content.blanks.some((b) => b.hint) && (
        <ul className="space-y-1 text-sm text-ink-500">
          {content.blanks.map((blank, index) =>
            blank.hint ? (
              <li key={index}>
                Blank {index + 1}: {blank.hint}
              </li>
            ) : null,
          )}
        </ul>
      )}
    </div>
  )
}
